// frontend/src/settings/ShiftPatternCsvImportModal.jsx
// 勤務パターンを CSV で一括登録するモーダル。
//
// 対応列 (ヘッダー、日本語/英語いずれも可):
//   勤務名            (name)                        [必須]
//   表示記号          (display_mark)                [必須]
//   開始時刻          (start_time)   HH:MM          省略時 "08:30"
//   終了時刻          (end_time)     HH:MM          省略時 "17:00"
//   色                (color)        #hex           省略時 "#3b82f6"
//   優先度            (priority)     int            省略時 1
//   必要人数          (required_count) int          省略時 0 (全曜日に適用)
//   曜日別必要人数    (required_by_day_of_week)   "月:5,火:5,水:5,木:5,金:5,土:0,日:0" 省略可、記載時は上位より優先
//   柔軟性            (flexibility)  fixed|flexible  省略時 flexible
//   最小必要数        (min_required) int            省略時 0
//   最大連勤          (max_consecutive) int         省略時 6
//   強制配置          (force_assign)   0|1          省略時 0
//   希望受付可        (can_receive_request) 0|1    省略時 0
//   希望グループ      (request_group)                省略可
//   希望グループラベル (request_group_label)        省略可
//   希望表示記号      (request_display_mark)        省略可
//   月間最大希望回数   (max_monthly_requests) int    省略可
//
// 既存の add アクションで 1 行ずつ API 投入 (= 失敗行はスキップ、進捗バー + 結果サマリ)

import { useState, useRef } from 'react';
import Papa from 'papaparse';
import { Upload, Download, X, Check, AlertCircle, Loader2 } from 'lucide-react';
import { manageShiftPatterns } from '../supabase';

const CSV_TEMPLATE = `勤務名,表示記号,開始時刻,終了時刻,色,優先度,必要人数,曜日別必要人数,柔軟性,最小必要数,最大連勤,強制配置,希望受付可,希望グループ,希望グループラベル,希望表示記号,月間最大希望回数
日勤,日,08:30,17:00,#3b82f6,1,5,"月:5,火:5,水:5,木:5,金:5,土:0,日:0",fixed,0,6,1,0,,,,
夜勤,夜,16:30,09:00,#8b5cf6,2,2,,fixed,0,2,1,0,,,,
公休ロ1,ロ,,,,,1,,flexible,0,6,0,1,kokyuu,公休,ロ,10
`;

const HEADER_ALIASES = {
  name:                    ['勤務名', 'name'],
  display_mark:            ['表示記号', 'short_name', 'display_mark', 'displayMark'],
  start_time:              ['開始時刻', 'start_time', 'startTime'],
  end_time:                ['終了時刻', 'end_time', 'endTime'],
  color:                   ['色', 'color'],
  priority:                ['優先度', 'priority'],
  required_count:          ['必要人数', 'required_count'],
  required_by_day_of_week: ['曜日別必要人数', 'required_by_day_of_week'],
  flexibility:             ['柔軟性', 'flexibility', 'flexibility_type', 'flexibilityType'],
  min_required:            ['最小必要数', 'min_required', 'minRequired'],
  max_consecutive:         ['最大連勤', 'max_consecutive', 'maxConsecutive'],
  force_assign:            ['強制配置', 'force_assign', 'forceAssign'],
  can_receive_request:     ['希望受付可', 'can_receive_request', 'canReceiveRequest'],
  request_group:           ['希望グループ', 'request_group', 'requestGroup'],
  request_group_label:     ['希望グループラベル', 'request_group_label', 'requestGroupLabel'],
  request_display_mark:    ['希望表示記号', 'request_display_mark', 'requestDisplayMark'],
  max_monthly_requests:    ['月間最大希望回数', 'max_monthly_requests', 'maxMonthlyRequests'],
};

const WEEKDAYS = ['月', '火', '水', '木', '金', '土', '日'];

const resolveHeader = (headerRow) => {
  const map = {};
  Object.entries(HEADER_ALIASES).forEach(([key, aliases]) => {
    const idx = headerRow.findIndex((h) =>
      aliases.some((a) => (h || '').trim().toLowerCase() === a.toLowerCase())
    );
    map[key] = idx >= 0 ? idx : null;
  });
  return map;
};

const parseWeekdayCounts = (str) => {
  // "月:5,火:5,水:5" → {月:5, 火:5, 水:5}
  if (!str) return null;
  const out = {};
  let hasValue = false;
  str.split(/[,、]/).forEach((pair) => {
    const m = pair.trim().match(/^(月|火|水|木|金|土|日)\s*[:：]\s*(\d+)$/);
    if (m) {
      out[m[1]] = Number(m[2]);
      hasValue = true;
    }
  });
  return hasValue ? out : null;
};

const parseBoolean = (val) => {
  if (val === '' || val == null) return null;
  const s = String(val).trim().toLowerCase();
  if (['1', 'true', 'yes', 'y', '○', 'o', 'on'].includes(s)) return true;
  if (['0', 'false', 'no', 'n', '×', 'x', 'off'].includes(s)) return false;
  return null;
};

const TIME_RE = /^\d{1,2}:\d{2}$/;

export default function ShiftPatternCsvImportModal({
  open, onClose, onSuccess, organizationId, existingNames = [],
}) {
  const [stage, setStage] = useState('select');
  const [rows, setRows] = useState([]);
  const [parseError, setParseError] = useState('');
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const [results, setResults] = useState([]);
  const fileInputRef = useRef(null);

  if (!open) return null;

  const reset = () => {
    setStage('select'); setRows([]); setParseError('');
    setProgress({ done: 0, total: 0 }); setResults([]);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleClose = () => {
    if (stage === 'importing') return;
    reset(); onClose?.();
  };

  const handleDownloadTemplate = () => {
    const bom = '﻿';
    const blob = new Blob([bom + CSV_TEMPLATE], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'shift_pattern_import_template.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleFile = (file) => {
    if (!file) return;
    setParseError('');
    Papa.parse(file, {
      skipEmptyLines: true,
      complete: (result) => {
        try {
          const data = result.data || [];
          if (data.length < 2) {
            setParseError('CSV にヘッダー行と 1 件以上のデータが必要です');
            return;
          }
          const headerRow = data[0];
          const headerMap = resolveHeader(headerRow);
          if (headerMap.name === null || headerMap.display_mark === null) {
            setParseError('必須列 (勤務名 / 表示記号) が見つかりません');
            return;
          }
          const seenNames = new Set();
          const parsed = data.slice(1).map((cols, i) => {
            const get = (key) => (headerMap[key] !== null ? (cols[headerMap[key]] || '').trim() : '');

            const name = get('name');
            const display_mark = get('display_mark');
            const start_time = get('start_time') || '08:30';
            const end_time = get('end_time') || '17:00';
            const colorRaw = get('color');
            const color = colorRaw && /^#[0-9a-fA-F]{3,8}$/.test(colorRaw) ? colorRaw : '#3b82f6';
            const priorityRaw = get('priority');
            const priority = priorityRaw ? Number(priorityRaw) : 1;
            const requiredCountRaw = get('required_count');
            const requiredCount = requiredCountRaw ? Number(requiredCountRaw) : 0;
            const weekdayCountStr = get('required_by_day_of_week');
            const weekdayCountParsed = parseWeekdayCounts(weekdayCountStr);
            // required_count は曜日マップ形式で保持 (DB の required_count と required_by_day_of_week 両方に同じ形)
            let requiredByDayOfWeek;
            if (weekdayCountParsed) {
              // 曜日別が指定されていればそれを使う (未指定の曜日は 0)
              requiredByDayOfWeek = {};
              WEEKDAYS.forEach(wd => { requiredByDayOfWeek[wd] = weekdayCountParsed[wd] ?? 0; });
            } else if (requiredCount > 0) {
              requiredByDayOfWeek = {};
              WEEKDAYS.forEach(wd => { requiredByDayOfWeek[wd] = requiredCount; });
            } else {
              requiredByDayOfWeek = {};
            }

            const flexibilityRaw = (get('flexibility') || 'flexible').toLowerCase();
            const flexibility_type = flexibilityRaw === 'fixed' ? 'fixed' : 'flexible';
            const min_required = Number(get('min_required') || '0');
            const max_consecutive = Number(get('max_consecutive') || '6');
            const force_assign = parseBoolean(get('force_assign')) === true;
            const can_receive_request = parseBoolean(get('can_receive_request')) === true;
            const request_group = get('request_group');
            const request_group_label = get('request_group_label');
            const request_display_mark = get('request_display_mark');
            const maxMonthlyRaw = get('max_monthly_requests');
            const max_monthly_requests = maxMonthlyRaw ? Number(maxMonthlyRaw) : null;

            const errors = [];
            if (!name) errors.push('勤務名 必須');
            if (!display_mark) errors.push('表示記号 必須');
            if (name && seenNames.has(name)) errors.push('CSV 内で勤務名が重複');
            if (name && existingNames.includes(name)) errors.push('この組織に同名が既に存在');
            if (start_time && !TIME_RE.test(start_time)) errors.push('開始時刻 形式不正');
            if (end_time && !TIME_RE.test(end_time)) errors.push('終了時刻 形式不正');
            if (priorityRaw && !Number.isFinite(priority)) errors.push('優先度 数値不正');
            if (weekdayCountStr && !weekdayCountParsed) errors.push('曜日別必要人数 形式不正 (例: 月:5,火:5)');
            if (can_receive_request && !request_group) errors.push('希望受付可=1 の場合、希望グループ必須');

            if (name) seenNames.add(name);

            return {
              rowNumber: i + 2,
              valid: errors.length === 0,
              errors,
              data: {
                name, display_mark, start_time, end_time, color, priority,
                required_count: requiredByDayOfWeek,
                required_by_day_of_week: requiredByDayOfWeek,
                flexibility_type, min_required, max_consecutive, force_assign,
                can_receive_request,
                request_group,
                request_group_label,
                request_display_mark,
                max_monthly_requests,
              },
            };
          });
          setRows(parsed);
          setStage('preview');
        } catch (e) {
          setParseError('CSV の解析に失敗しました: ' + e.message);
        }
      },
      error: (e) => {
        setParseError('CSV の読み込みに失敗しました: ' + e.message);
      },
    });
  };

  const validCount = rows.filter(r => r.valid).length;
  const invalidCount = rows.length - validCount;

  const runImport = async () => {
    const targets = rows.filter(r => r.valid);
    if (targets.length === 0) return;
    setStage('importing');
    setProgress({ done: 0, total: targets.length });
    const outcomes = [];
    for (let i = 0; i < targets.length; i++) {
      const r = targets[i];
      try {
        await manageShiftPatterns('add', { organizationId, ...r.data });
        outcomes.push({ rowNumber: r.rowNumber, name: r.data.name, ok: true, message: '登録成功' });
      } catch (e) {
        outcomes.push({ rowNumber: r.rowNumber, name: r.data.name, ok: false, message: e.message || '不明なエラー' });
      }
      setProgress({ done: i + 1, total: targets.length });
    }
    setResults(outcomes);
    setStage('done');
    const okN = outcomes.filter(o => o.ok).length;
    if (okN > 0) onSuccess?.(okN);
  };

  return (
    <div style={styles.overlay} onClick={handleClose}>
      <div style={styles.card} onClick={(e) => e.stopPropagation()}>
        <div style={styles.header}>
          <h3 style={styles.title}>
            <Upload size={18} style={{ verticalAlign: 'middle', marginRight: 6 }} />
            CSV で勤務パターンを一括登録
          </h3>
          <button onClick={handleClose} style={styles.closeBtn} disabled={stage === 'importing'}>
            <X size={20} />
          </button>
        </div>

        {stage === 'select' && (
          <div style={styles.body}>
            <p style={styles.helper}>
              必須列: <strong>勤務名 / 表示記号</strong><br />
              主な任意列: 開始時刻 / 終了時刻 / 色 / 優先度 / 必要人数 / 曜日別必要人数 / 柔軟性 / 希望受付可 / 希望グループ 等<br />
              高度な設定 (スキル条件 / 連鎖 / 休暇調整 等) は作成後に UI で微調整してください。
            </p>
            <button onClick={handleDownloadTemplate} style={styles.ghostBtn}>
              <Download size={14} />テンプレートをダウンロード
            </button>
            <div style={{ marginTop: 20 }}>
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv,text/csv"
                onChange={(e) => handleFile(e.target.files?.[0])}
                style={styles.fileInput}
              />
            </div>
            {parseError && (
              <div style={styles.errorBox}>
                <AlertCircle size={14} /> {parseError}
              </div>
            )}
          </div>
        )}

        {stage === 'preview' && (
          <div style={styles.body}>
            <div style={styles.summary}>
              <span>総行数: <strong>{rows.length}</strong></span>
              <span style={{ color: '#16a34a' }}>有効: <strong>{validCount}</strong></span>
              <span style={{ color: '#dc2626' }}>エラー: <strong>{invalidCount}</strong></span>
            </div>
            <div style={styles.tableWrap}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.th}>行</th>
                    <th style={styles.th}>勤務名</th>
                    <th style={styles.th}>表示</th>
                    <th style={styles.th}>時刻</th>
                    <th style={styles.th}>必要人数</th>
                    <th style={styles.th}>柔軟性</th>
                    <th style={styles.th}>希望</th>
                    <th style={styles.th}>検証</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r, i) => (
                    <tr key={i} style={r.valid ? styles.rowOk : styles.rowNg}>
                      <td style={styles.td}>{r.rowNumber}</td>
                      <td style={styles.td}>{r.data.name}</td>
                      <td style={styles.td}>{r.data.display_mark}</td>
                      <td style={styles.td}>{r.data.start_time}-{r.data.end_time}</td>
                      <td style={styles.td}>
                        {Object.values(r.data.required_by_day_of_week || {}).join('/')}
                      </td>
                      <td style={styles.td}>{r.data.flexibility_type}</td>
                      <td style={styles.td}>{r.data.can_receive_request ? `○ ${r.data.request_group || ''}` : '-'}</td>
                      <td style={styles.td}>
                        {r.valid
                          ? <span style={styles.ok}><Check size={12} /> OK</span>
                          : <span style={styles.ng} title={r.errors.join(' / ')}>
                              <AlertCircle size={12} /> {r.errors[0]}
                              {r.errors.length > 1 && ` (+${r.errors.length - 1})`}
                            </span>
                        }
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div style={styles.footer}>
              <button onClick={reset} style={styles.secondaryBtn}>戻る</button>
              <button
                onClick={runImport}
                disabled={validCount === 0}
                style={{
                  ...styles.primaryBtn,
                  opacity: validCount === 0 ? 0.5 : 1,
                  cursor: validCount === 0 ? 'not-allowed' : 'pointer',
                }}
              >
                <Upload size={14} />
                有効な {validCount} 件を登録する
              </button>
            </div>
          </div>
        )}

        {stage === 'importing' && (
          <div style={styles.body}>
            <div style={styles.progressWrap}>
              <Loader2 size={32} style={{ animation: 'spin 1s linear infinite', color: '#4338ca' }} />
              <div style={{ marginTop: 12, fontSize: '1.1rem', fontWeight: 600 }}>
                登録中... {progress.done} / {progress.total}
              </div>
              <div style={styles.progressBar}>
                <div style={{
                  ...styles.progressFill,
                  width: `${(progress.done / progress.total) * 100}%`,
                }} />
              </div>
            </div>
            <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
          </div>
        )}

        {stage === 'done' && (
          <div style={styles.body}>
            <div style={styles.summary}>
              <span style={{ color: '#16a34a' }}>成功: <strong>{results.filter(r => r.ok).length}</strong></span>
              <span style={{ color: '#dc2626' }}>失敗: <strong>{results.filter(r => !r.ok).length}</strong></span>
            </div>
            <div style={styles.tableWrap}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.th}>行</th>
                    <th style={styles.th}>勤務名</th>
                    <th style={styles.th}>結果</th>
                  </tr>
                </thead>
                <tbody>
                  {results.map((r, i) => (
                    <tr key={i} style={r.ok ? styles.rowOk : styles.rowNg}>
                      <td style={styles.td}>{r.rowNumber}</td>
                      <td style={styles.td}>{r.name}</td>
                      <td style={styles.td}>
                        {r.ok
                          ? <span style={styles.ok}><Check size={12} /> {r.message}</span>
                          : <span style={styles.ng}><AlertCircle size={12} /> {r.message}</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div style={styles.footer}>
              <button onClick={reset} style={styles.secondaryBtn}>別の CSV を読み込む</button>
              <button onClick={handleClose} style={styles.primaryBtn}>閉じる</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

const styles = {
  overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10002, padding: '1rem' },
  card: { background: '#fff', borderRadius: 12, width: '100%', maxWidth: 920, maxHeight: '90vh', overflow: 'hidden', display: 'flex', flexDirection: 'column', boxShadow: '0 12px 40px rgba(0,0,0,0.2)' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 20px', borderBottom: '1px solid #e5e7eb' },
  title: { margin: 0, fontSize: '1.1rem', fontWeight: 700, color: '#111827' },
  closeBtn: { background: 'transparent', border: 'none', cursor: 'pointer', color: '#6b7280', padding: 4 },
  body: { padding: '18px 20px', overflowY: 'auto', flex: 1 },
  helper: { fontSize: '0.9rem', color: '#4b5563', lineHeight: 1.7, margin: '0 0 1rem 0' },
  ghostBtn: { display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 12px', border: '1px solid #d1d5db', background: '#fff', borderRadius: 6, cursor: 'pointer', fontSize: '0.85rem', color: '#374151' },
  fileInput: { width: '100%', padding: '12px', border: '2px dashed #cbd5e1', borderRadius: 8, background: '#f8fafc', cursor: 'pointer' },
  errorBox: { marginTop: 12, padding: '10px 14px', background: '#fef2f2', border: '1px solid #fca5a5', color: '#991b1b', borderRadius: 6, display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.85rem' },
  summary: { display: 'flex', gap: 20, padding: '10px 14px', background: '#f8fafc', borderRadius: 6, marginBottom: 12, fontSize: '0.9rem' },
  tableWrap: { overflowX: 'auto', maxHeight: '50vh', overflowY: 'auto' },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' },
  th: { padding: '8px 10px', textAlign: 'left', background: '#f1f5f9', borderBottom: '1px solid #e2e8f0', fontWeight: 600, color: '#475569', position: 'sticky', top: 0 },
  td: { padding: '8px 10px', borderBottom: '1px solid #f3f4f6' },
  rowOk: { background: '#fff' },
  rowNg: { background: '#fef2f2' },
  ok: { display: 'inline-flex', alignItems: 'center', gap: 4, color: '#16a34a', fontWeight: 600, fontSize: '0.8rem' },
  ng: { display: 'inline-flex', alignItems: 'center', gap: 4, color: '#dc2626', fontWeight: 600, fontSize: '0.8rem' },
  footer: { display: 'flex', justifyContent: 'flex-end', gap: 8, padding: '12px 0 0 0', borderTop: '1px solid #f3f4f6', marginTop: 14 },
  primaryBtn: { display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 16px', background: 'linear-gradient(135deg, #4338ca 0%, #6366f1 100%)', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: '0.9rem', fontWeight: 600 },
  secondaryBtn: { padding: '8px 16px', background: '#f3f4f6', color: '#374151', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: '0.9rem', fontWeight: 500 },
  progressWrap: { display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '32px 0' },
  progressBar: { marginTop: 14, width: '100%', maxWidth: 400, height: 10, background: '#e5e7eb', borderRadius: 999, overflow: 'hidden' },
  progressFill: { height: '100%', background: 'linear-gradient(90deg, #4338ca, #6366f1)', transition: 'width 0.2s ease' },
};
